import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiUsers, FiShoppingCart, FiCalendar, FiShoppingBag, FiActivity, FiAward, FiPieChart, FiRefreshCw } from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const Analytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      const response = await api.get('/vendor-owner/dashboard/stats');
      if (response.data.success) {
        setAnalyticsData(response.data.data);
        toast.success('Analytics data refreshed successfully!');
      }
    } catch (error) {
      toast.error('Failed to fetch analytics data');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric'
    });
  };

  const COLORS = ['#2596be', '#4caf50', '#ff9800', '#f44336', '#9c27b0'];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <FiActivity className="text-indigo-600" />
            Analytics & Insights
          </h1>
          <p className="text-gray-600">Comprehensive performance metrics and business intelligence</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiActivity className="text-gray-400 text-3xl" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">No analytics data available</h3>
          <p className="text-gray-600">Unable to load analytics data at this time.</p>
        </div>
      </div>
    );
  }

  const { overview, ordersByStatus, last7DaysRevenue, topRestaurants } = analyticsData;

  const statusData = Object.keys(ordersByStatus).map(status => ({
    name: status.charAt(0).toUpperCase() + status.slice(1),
    value: ordersByStatus[status]
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <FiActivity className="text-indigo-600" />
              Analytics & Insights
            </h1>
            <p className="text-gray-600">Comprehensive performance metrics and business intelligence</p>
          </div>
          <div className="flex gap-3">
            <button 
              className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
              onClick={fetchAnalyticsData}
            >
              <FiRefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <FiShoppingBag size={24} />
            </div>
            <div className="w-8 h-8 bg-green-400 rounded-full flex items-center justify-center">
              <FiTrendingUp size={14} />
            </div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-1">{overview.totalOrders}</div>
            <div className="text-purple-100 text-sm mb-3">Total Orders</div>
            <div className="inline-flex px-3 py-1 bg-white/20 rounded-full text-xs font-medium">
              This Week: {overview.weekOrders}
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <FaRupeeSign size={24} />
            </div>
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
              <FiTrendingUp size={14} className="text-green-600" />
            </div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-1">{formatCurrency(overview.totalRevenue)}</div>
            <div className="text-green-100 text-sm mb-3">Total Revenue</div>
            <div className="inline-flex px-3 py-1 bg-white/20 rounded-full text-xs font-medium">
              This Week: {formatCurrency(overview.weekRevenue)}
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <FiTrendingUp size={24} />
            </div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-1">{formatCurrency(overview.averageOrderValue)}</div>
            <div className="text-orange-100 text-sm mb-3">Avg Order Value</div>
            <div className="inline-flex px-3 py-1 bg-white/20 rounded-full text-xs font-medium">
              Per Transaction
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <FiUsers size={24} />
            </div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-1">{overview.totalRestaurants}</div>
            <div className="text-blue-100 text-sm mb-3">Restaurants</div>
            <div className="inline-flex px-3 py-1 bg-white/20 rounded-full text-xs font-medium">
              {overview.activeRestaurants} Active
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Revenue & Orders Trend */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <FiActivity className="text-indigo-600" />
              Revenue & Orders Trend
            </h3>
            <p className="text-gray-600 text-sm">Performance over the last 7 days</p>
          </div>
          <div className="h-80">
            {last7DaysRevenue && last7DaysRevenue.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={last7DaysRevenue} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2596be" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#2596be" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#4caf50" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis 
                    dataKey="_id" 
                    tickFormatter={formatDate}
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                  <YAxis tick={{ fill: '#666', fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={formatDate}
                    contentStyle={{ 
                      background: 'white', 
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend iconType="circle" />
                  <Area type="monotone" dataKey="revenue" stroke="#2596be" fillOpacity={1} fill="url(#colorRevenue)" name="Revenue" strokeWidth={2} />
                  <Area type="monotone" dataKey="orders" stroke="#4caf50" fillOpacity={1} fill="url(#colorOrders)" name="Orders" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center flex-col text-center">
                <FiActivity size={48} className="text-gray-300 mb-4" />
                <p className="text-gray-600 font-medium mb-2">No revenue data available yet</p>
                <p className="text-gray-400 text-sm">Complete some orders to see the trend</p>
              </div>
            )}
          </div>
        </div>

        {/* Orders by Status */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <FiPieChart className="text-indigo-600" />
              Orders by Status
            </h3>
            <p className="text-gray-600 text-sm">Distribution of order statuses</p>
          </div>
          <div className="h-80">
            {statusData && statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: 'white', 
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center flex-col text-center">
                <FiPieChart size={48} className="text-gray-300 mb-4" />
                <p className="text-gray-600 font-medium mb-2">No order status data</p>
                <p className="text-gray-400 text-sm">Place some orders to see the distribution</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Performing Restaurants */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <FiAward className="text-indigo-600" />
            Top Performing Restaurants
          </h3>
          <p className="text-gray-600 text-sm">Ranked by total revenue generated</p>
        </div>
        {topRestaurants && topRestaurants.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {topRestaurants.map((restaurant, index) => (
              <div key={restaurant.restaurantId} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                    index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-600' : 'bg-gray-300'
                  }`}>
                    #{index + 1}
                  </div>
                  <h4 className="font-semibold text-gray-900">{restaurant.restaurantName}</h4>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1 text-gray-600">
                    <FiShoppingBag size={14} />
                    {restaurant.totalOrders} orders
                  </span>
                  <span className="flex items-center gap-1 font-medium text-green-600">
                    <FaRupeeSign size={14} />
                    {formatCurrency(restaurant.totalRevenue)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FiAward size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 font-medium mb-2">No performance data available</p>
            <p className="text-gray-400 text-sm">Complete orders to see top performing restaurants</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
